import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ENUMS, FINANCE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

/**
 * @author @muhammedburakak
 */

const GenerateCODReportModal = NiceModal.create(() => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const { showError } = useAlert();

	const defaultColSpan = {
		md: 6
	};

	const [isLoading, setIsLoading] = useState(false);

	const schema = useMemo(() =>
		yup.object().shape({
			date: yup
				.object()
				.shape({
					start: yup.date().required(),
					end: yup.date().required()
				})
				.required(),
			clientPlan: yup.string().nullable().required()
		})
	);

	const formMethods = useForm({
		resolver: yupResolver(schema)
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, errors }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		setIsLoading(true);

		try {
			const response = await axios.post(FINANCE.GENERATE_COD_REPORT, {
				startDate: data.date.start,
				endDate: data.date.end,
				clientPlan: data.clientPlan
			});
			if (response.status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.NEW_COD_REPORTS));
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
			modal.remove();
		}
	};

	return (
		<Modal maxWidth="small" fullwidth className="modal-bankTransferForm">
			<ModalTitle>
				<Typography variant="p1">generateCODReport</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="clientPlan" isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							name="clientPlan"
							enumName={ENUMS.V2_CLIENT_TYPES}
							setValue={(value) => {
								setValue(`clientPlan`, value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`clientPlan`)}
						/>
					</FormItem>

					<FormItem label="date" colSpan={defaultColSpan} required>
						<DateInput
							name={'date'}
							value={watch('date')}
							setValue={(value) => {
								setValue('date', value, { shouldValidate: true, shouldDirty: true });
							}}
							errorMessage={errors?.date?.start?.message || errors?.date?.end?.message}
							rangeMode
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						if (isDirty && !isSubmitSuccessful && !isSubmitting) {
							NiceModal.show(ConfirmationModal, {
								yesAction: () => {
									modal.remove();
								}
							});
						} else {
							modal.remove();
						}
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="generate"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default GenerateCODReportModal;
